import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, InputNumber, Select, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_TT_BAG, CreateTTBagInput, TTBag } from '../graphql/mutations/createTTBag';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { useTranslation } from 'react-i18next';
import { GET_ALL_CARRIER_OPTIONS, GetAllCarrierOptionsResponse } from '../graphql/queries/getAllCarrierOptions';
import { BusinessType } from '../graphql/types/BagTagType';
import {
    GET_TT_LOCATION_OPTIONS,
    GetTtLocationOptionsResponse,
    TT_LOCATION_TYPE,
} from '../graphql/queries/getTtLocationOptions';
import {
    GET_EXTERNAL_STATION_SORTING_CODES,
    GetExternalStationSortingCodesResponse,
} from '../graphql/queries/getExternalStationSortingCodes';
import { ExternalStationType } from '../../../../enums/TmsEnum';

/** 表单值：Yes→分拣码中段 H，No→中段 G，ALL→G 与 H 各一条 */
const TEMU_HAZMAT_VALUES = ['YES', 'NO', 'ALL'] as const;
/** Type 选项：GA / SCF */
const TEMU_TYPE_OPTIONS = ['GA', 'SCF'] as const;
/** DHLe 尾程商固定单段目的地 */
const DHLE_FIXED_DEST = 'LA';
/** DHLe 尾程商 carrier value，对应 api-server FinalMileCarrier.DHL_ECOMMERCE(26) */
const DHL_ECOMMERCE_CARRIER_CODE = 26;

export interface NewTemuBagFormProps {
    visible: boolean;
    onClose: () => void;
    onSubmitSuccess: (bags: TTBag[], printCopies: number) => void;
}

function expandHazmat(haz: string): string[] {
    if (haz === 'ALL') return ['G', 'H'];
    if (haz === 'YES') return ['H'];
    if (haz === 'NO') return ['G'];
    return [];
}

function expandDestinations(dests: string[], allDestCodes: string[]): string[] {
    if (dests.includes('ALL')) return allDestCodes;
    return dests;
}

function buildSortingCodes(
    type: string,
    haz: string,
    dests: string[],
    allDestCodes: string[],
    largeBoxCode?: string
): string[] {
    const codes: string[] = [];
    for (const hz of expandHazmat(haz)) {
        for (const dest of expandDestinations(dests, allDestCodes)) {
            if (type === 'SCF' && largeBoxCode) {
                codes.push(`${type}-${hz}-${dest}-${largeBoxCode}`);
            } else {
                codes.push(`${type}-${hz}-${dest}`);
            }
        }
    }
    return codes;
}

const NewTemuBagForm: React.FC<NewTemuBagFormProps> = ({ visible, onClose, onSubmitSuccess }) => {
    const { currentWarehouse } = useWarehouseContext();
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const submittingRef = useRef(false);

    const { data: carrierData, loading: carrierLoading } = useQuery<GetAllCarrierOptionsResponse>(
        GET_ALL_CARRIER_OPTIONS,
        { skip: !visible }
    );

    // GA/SCF 目的地：按站点类型 + 当前选中尾程商过滤，且后端会合并 TEMU_HANDOVER(3) 中相同尾程商的站点
    // 未选尾程商（如首次渲染尚未回填 USPS）时跳过查询，避免拉出全量数据
    const watchedCarrier = Form.useWatch('temuCarrier', form) as number | undefined;
    const { data: gaDestData } = useQuery<GetExternalStationSortingCodesResponse>(
        GET_EXTERNAL_STATION_SORTING_CODES,
        {
            variables: { stationType: ExternalStationType.TEMU_GA, carrierCode: watchedCarrier, warehouseId: currentWarehouse?.id },
            skip: !visible || watchedCarrier == null,
        }
    );

    const { data: scfDestData } = useQuery<GetExternalStationSortingCodesResponse>(
        GET_EXTERNAL_STATION_SORTING_CODES,
        {
            variables: { stationType: ExternalStationType.TEMU_SCF, carrierCode: watchedCarrier, warehouseId: currentWarehouse?.id },
            skip: !visible || watchedCarrier == null,
        }
    );

    // DHLe 分支：直接查 stationType=3 且 carrier=DHLe，与新版 gql 契约保持一致（carrierCode 必填）
    const { data: dhleDestData } = useQuery<GetExternalStationSortingCodesResponse>(
        GET_EXTERNAL_STATION_SORTING_CODES,
        {
            variables: { stationType: ExternalStationType.TEMU_HANDOVER, carrierCode: DHL_ECOMMERCE_CARRIER_CODE, warehouseId: currentWarehouse?.id },
            skip: !visible,
        }
    );

    const { data: largeBoxData } = useQuery<GetTtLocationOptionsResponse>(
        GET_TT_LOCATION_OPTIONS,
        { variables: { locationType: TT_LOCATION_TYPE.TEMU_LARGE_BOX }, skip: !visible }
    );

    const largeBoxOptions = useMemo(() => {
        return (largeBoxData?.getTtLocationOptions || []).map((d) => ({ value: d.code, label: d.name }));
    }, [largeBoxData]);

    const temuCarrierCode = useMemo(() => {
        const list = carrierData?.getAllCarrierOptions || [];
        const norm = (s: string | undefined) => s?.replace(/®/g, '').trim().toUpperCase() ?? '';
        return (
            list.find((c) => norm(c.name) === 'USPS')?.value ??
            list.find((c) => c.value === 1)?.value ??
            list.find((c) => {
                const n = norm(c.name);
                return n.includes('USPS') && !n.includes('TM');
            })?.value
        );
    }, [carrierData]);

    const [sortingPreview, setSortingPreview] = useState('');

    // watchedCarrier 已在上方 useQuery 之前声明（用于 GA/SCF 目的地查询过滤）
    const watchedType = Form.useWatch('temuType', form);
    const watchedHaz = Form.useWatch('temuHazmat', form);
    const watchedDest = Form.useWatch('temuDest', form);
    const watchedLargeBox = Form.useWatch('temuLargeBoxCode', form);

    const isDhle = watchedCarrier === DHL_ECOMMERCE_CARRIER_CODE;
    const isScf = watchedType === 'SCF';
    const isGa = watchedType === 'GA';

    // 尾程商可选项：仅 USPS（真实 value）+ DHLe（占位/禁用）
    const carrierOptions = useMemo(() => {
        const opts: { value: number; label: string }[] = [];
        if (temuCarrierCode != null) {
            const usps = carrierData?.getAllCarrierOptions.find((c) => c.value === temuCarrierCode);
            opts.push({ value: temuCarrierCode, label: usps?.name ?? 'USPS' });
        }
        const dhle = carrierData?.getAllCarrierOptions.find((c) => c.value === DHL_ECOMMERCE_CARRIER_CODE);
        opts.push({ value: DHL_ECOMMERCE_CARRIER_CODE, label: dhle?.name ?? 'DHL_ECOMMERCE' });
        return opts;
    }, [carrierData, temuCarrierCode]);

    const gaDestOptions = useMemo(() => {
        const codes = gaDestData?.getExternalStationSortingCodes || [];
        const opts = codes.map((code) => ({ value: code, label: code }));
        return [{ value: 'ALL', label: 'ALL' }, ...opts];
    }, [gaDestData]);

    const scfDestOptions = useMemo(() => {
        const codes = scfDestData?.getExternalStationSortingCodes || [];
        const opts = codes.map((code) => ({ value: code, label: code }));
        return [{ value: 'ALL', label: 'ALL' }, ...opts];
    }, [scfDestData]);

    const destOptions = useMemo(() => isGa ? gaDestOptions : scfDestOptions, [isGa, gaDestOptions, scfDestOptions]);

    // DHLe 目的地：取 external station(stationType=3)；0 个→默认 LA 不可改，1 个→该值不可改，多个→可选
    const dhleDestCodes = useMemo(() => dhleDestData?.getExternalStationSortingCodes || [], [dhleDestData]);
    const dhleDestOptions = useMemo(() => {
        if (dhleDestCodes.length === 0) return [{ value: DHLE_FIXED_DEST, label: DHLE_FIXED_DEST }];
        return dhleDestCodes.map((code) => ({ value: code, label: code }));
    }, [dhleDestCodes]);
    const dhleDestDisabled = dhleDestCodes.length <= 1;

    const allDestCodes = useMemo(() => {
        if (isGa) {
            return gaDestData?.getExternalStationSortingCodes || [];
        }
        return scfDestData?.getExternalStationSortingCodes || [];
    }, [isGa, gaDestData, scfDestData]);

    // carrier 选项异步就绪后，默认回填为 USPS（保持改动前行为）
    useEffect(() => {
        if (temuCarrierCode != null && form.getFieldValue('temuCarrier') == null) {
            form.setFieldsValue({ temuCarrier: temuCarrierCode });
        }
    }, [temuCarrierCode, form]);

    // 切换 GA/SCF 时清空目的地，让用户重新选择；DHLe 目的地固定 LA，不参与清空
    useEffect(() => {
        if (isDhle) return;
        form.setFieldsValue({ temuDest: [] });
    }, [isGa, isDhle, form]);

    // 选中 DHLe 时按 external station(stationType=3) 结果设目的地：0 个→默认 LA，1 个→该值（均不可改），多个→清空待用户选
    useEffect(() => {
        if (!isDhle) return;
        if (dhleDestCodes.length === 0) {
            form.setFieldsValue({ temuDest: [DHLE_FIXED_DEST] });
        } else if (dhleDestCodes.length === 1) {
            form.setFieldsValue({ temuDest: [dhleDestCodes[0]] });
        } else {
            form.setFieldsValue({ temuDest: [] });
        }
    }, [isDhle, dhleDestCodes, form]);

    useEffect(() => {
        if (isDhle) {
            const dests: string[] = watchedDest || [];
            if (dests.length === 0) {
                setSortingPreview('');
            } else if (dests.length === 1) {
                setSortingPreview(dests[0]);
            } else {
                setSortingPreview(
                    String(
                        t('wms.bags.temuBag.sortingPreviewMultiple', {
                            first: dests[0],
                            last: dests[dests.length - 1],
                            count: dests.length,
                        })
                    )
                );
            }
            return;
        }
        if (!watchedType || !watchedHaz || !watchedDest || watchedDest.length === 0) {
            setSortingPreview('');
            return;
        }
        if (isScf && !watchedLargeBox) {
            setSortingPreview('');
            return;
        }
        const codes = buildSortingCodes(watchedType, watchedHaz, watchedDest, allDestCodes, watchedLargeBox);
        if (codes.length === 0) {
            setSortingPreview('');
            return;
        }
        if (codes.length === 1) {
            setSortingPreview(codes[0]);
        } else {
            setSortingPreview(
                String(
                    t('wms.bags.temuBag.sortingPreviewMultiple', {
                        first: codes[0],
                        last: codes[codes.length - 1],
                        count: codes.length,
                    })
                )
            );
        }
    }, [isDhle, watchedType, watchedHaz, watchedDest, watchedLargeBox, isScf, allDestCodes, t]);

    const [createTTBag, { loading: creating }] = useMutation(CREATE_TT_BAG);

    const handleSubmit = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        try {
            const values = await form.validateFields();
            if (!currentWarehouse?.id) {
                message.error(t('wms.bags.messages.pleaseSelectWarehouse'));
                return;
            }
            if (!isDhle && temuCarrierCode == null) {
                message.error(t('wms.bags.temuBag.carrierNotConfigured'));
                return;
            }

            const codes = isDhle
                ? (values.temuDest ?? [])
                : buildSortingCodes(values.temuType, values.temuHazmat, values.temuDest, allDestCodes, values.temuLargeBoxCode);
            if (codes.length === 0) {
                message.error(t('wms.bags.temuBag.noSortingCodes'));
                return;
            }

            const allCreatedBags: TTBag[] = [];
            let hasError = false;
            const copyNum = values.bagQuantity || 1;
            const printCopies = values.printCopies || 1;

            for (const sortingCode of codes) {
                const parts = sortingCode.split('-');
                const input: CreateTTBagInput = {
                    from: 'TEMU',
                    to: undefined,
                    nextStop: sortingCode,
                    sortingCode,
                    warehouseId: currentWarehouse.id,
                    businessType: BusinessType.TEMU_SORT,
                    copyNum,
                    carrierCode: values.temuCarrier ?? temuCarrierCode,
                };
                const result = await createTTBag({ variables: { input } });
                if (result.data?.result.errorMessage) {
                    message.error(result.data.result.errorMessage);
                    hasError = true;
                    break;
                }
                if (result.data?.result.bags) {
                    allCreatedBags.push(...result.data.result.bags);
                }
            }

            if (!hasError && allCreatedBags.length > 0) {
                message.success(t('wms.bags.messages.successfullyCreatedTags', { count: allCreatedBags.length }));
                onSubmitSuccess(allCreatedBags, printCopies);
                onClose();
            }
        } catch (e) {
            console.error(e);
            message.error(t('wms.bags.messages.failedToCreateTags'));
        } finally {
            submittingRef.current = false;
        }
    };

    return (
        <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 7 }}
            wrapperCol={{ span: 17 }}
            initialValues={{
                bagQuantity: 1,
                printCopies: 1,
                temuType: 'GA',
                temuHazmat: 'ALL',
                temuDest: [],
            }}
        >
            <Form.Item name="temuCarrier" label={t('wms.bags.pageDetails.carrier')} rules={[{ required: true }]}>
                <Select loading={carrierLoading} options={carrierOptions} />
            </Form.Item>

            {!isDhle && (
                <>
                    <Form.Item name="temuType" label={t('wms.bags.temuBag.type')} rules={[{ required: true }]}>
                        <Select options={TEMU_TYPE_OPTIONS.map((v) => ({ value: v, label: v }))} />
                    </Form.Item>

                    <Form.Item name="temuHazmat" label={t('wms.bags.temuBag.hazmat')} rules={[{ required: true }]}>
                        <Select
                            options={TEMU_HAZMAT_VALUES.map((v) => ({ value: v, label: v }))}
                            placeholder={t('wms.bags.temuBag.hazmatPlaceholder')}
                        />
                    </Form.Item>
                </>
            )}

            <Form.Item name="temuDest" label={t('wms.bags.pageDetails.destination')} rules={[{ required: true }]}>
                <Select
                    mode="multiple"
                    options={isDhle ? dhleDestOptions : destOptions}
                    disabled={isDhle && dhleDestDisabled}
                    placeholder={t('wms.bags.temuBag.destinationPlaceholder')}
                />
            </Form.Item>

            {!isDhle && isScf && (
                <Form.Item name="temuLargeBoxCode" label={t('wms.bags.temuBag.largeBoxCode')} rules={[{ required: true }]}>
                    <Select options={largeBoxOptions} />
                </Form.Item>
            )}

            <Form.Item label={t('wms.bags.pageDetails.sortingCode')}>
                <span style={{ wordBreak: 'break-all' }}>{sortingPreview || '-'}</span>
            </Form.Item>

            <Form.Item name="bagQuantity" label={t('wms.bags.pageDetails.bagQuantity')} rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="printCopies" label={t('wms.bags.pageDetails.printCopies')} rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <div className="flex justify-end gap-2">
                <Button onClick={onClose}>{t('components.button.cancel')}</Button>
                <Button type="primary" loading={creating} onClick={handleSubmit}>
                    {t('components.button.create')}
                </Button>
            </div>
        </Form>
    );
};

export default NewTemuBagForm;